import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetTracingContactAttemptsByPatientQueryKey,
  useCreateContactTracingSmsAttemptByPatient,
} from '../../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { useGetPatientByIdSuspense } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';

interface RecipientOption {
  id: string;
  label: string;
  phone: string;
  /** Nur gesetzt, wenn die Nummer zu einem Adresskontakt gehört. Ohne Wert geht die SMS direkt an den Patienten. */
  contactId?: string;
}

interface FormValues {
  text: string;
  recipientId: string | null;
}

export function SendSmsDialog({ patientId, open, onClose }: { patientId: string; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: patient } = useGetPatientByIdSuspense(patientId);

  const recipientOptions = useMemo<RecipientOption[]>(() => {
    // Weitere Nummern (z.B. Privat, Geschäft, Adresskontakte) können hier ergänzt werden.
    const candidates: { id: string; label: string; phone?: string | null; contactId?: string }[] = [
      { id: 'mobile', label: 'Telefon Nr. Mobil', phone: patient.mobilePhoneNumber || patient.phone },
    ];

    return candidates.flatMap(({ phone, ...candidate }) => (phone ? [{ ...candidate, phone }] : []));
  }, [patient.mobilePhoneNumber, patient.phone]);

  const defaultRecipientId = recipientOptions[0]?.id ?? null;

  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      text: '',
      recipientId: defaultRecipientId,
    },
  });

  const createSmsMutation = useCreateContactTracingSmsAttemptByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetTracingContactAttemptsByPatientQueryKey(patientId),
        });
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Versenden der SMS ist ein Fehler aufgetreten');
      },
    },
  });

  useEffect(() => {
    if (open) {
      reset({ text: '', recipientId: defaultRecipientId });
    }
  }, [open, defaultRecipientId, reset]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700 }}>
        <ModalClose />
        <Typography level={'h4'}>
          SMS senden ({patient.lastName} {patient.firstName})
        </Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) => {
            const recipient = recipientOptions.find((option) => option.id === data.recipientId);
            createSmsMutation.mutate({
              patientId: patientId,
              data: {
                text: data.text,
                recipientContactId: recipient?.contactId,
              },
            });
          })}>
          <Stack gap={2}>
            <FormControl error={!!errors.text}>
              <FormLabel>
                <Typography>Nachricht *</Typography>
              </FormLabel>
              <Textarea {...register('text', { required: true })} minRows={2} maxRows={4} />
              {errors.text && <FormHelperText>Nachricht muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormControl error={!!errors.recipientId}>
              <FormLabel>
                <Typography>Empfänger *</Typography>
              </FormLabel>
              <Controller
                name={'recipientId'}
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value ?? null}
                    onChange={(_, selected) => onChange(selected)}
                    disabled={!recipientOptions.length}
                    placeholder={recipientOptions.length ? undefined : 'Keine Telefonnummer erfasst'}>
                    {recipientOptions.map((option) => (
                      <Option value={option.id} key={option.id}>
                        {option.label}: {option.phone}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.recipientId && <FormHelperText>Empfänger muss angegeben werden</FormHelperText>}
            </FormControl>

            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createSmsMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
